// src/components/DocumentPreviewDialog/index.tsx
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Checkbox,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { fetchDocumentPreview } from 'modules/chat/api';
import { buildHeaderTree, getDescendantIds, HeaderTreeNode } from 'utils/headerTree';

interface DocumentPreviewDialogProps {
  open: boolean;
  documentId: string | null;
  documentName?: string;
  /** Текущий выбор; undefined = весь документ */
  selectedHeaders?: string[];
  /** undefined в onApply = весь документ */
  onApply: (headers: string[] | undefined) => void;
  onClose: () => void;
}

interface TreeRowProps {
  node: HeaderTreeNode;
  depth: number;
  checked: Set<string>;
  collapsed: Set<string>;
  activeId: string | null;
  onToggleCheck: (node: HeaderTreeNode) => void;
  onToggleCollapse: (id: string) => void;
  onActivate: (id: string) => void;
}

const TreeRow = ({
  node,
  depth,
  checked,
  collapsed,
  activeId,
  onToggleCheck,
  onToggleCollapse,
  onActivate,
}: TreeRowProps) => {
  const descendants = getDescendantIds(node);
  const subtree = [node.id, ...descendants];
  const checkedCount = subtree.filter((id) => checked.has(id)).length;
  const allChecked = checkedCount === subtree.length;
  const indeterminate = checkedCount > 0 && !allChecked;
  const isCollapsed = collapsed.has(node.id);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: depth * 2,
          py: 0.25,
          cursor: 'pointer',
          borderRadius: 1,
          bgcolor: activeId === node.id ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => onActivate(node.id)}
      >
        {node.children.length > 0 ? (
          <IconButton
            size="small"
            sx={{ p: 0.25 }}
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse(node.id);
            }}
          >
            {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          </IconButton>
        ) : (
          <Box sx={{ width: 22 }} />
        )}
        <Checkbox
          size="small"
          sx={{ p: 0.5 }}
          checked={allChecked}
          indeterminate={indeterminate}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleCheck(node)}
        />
        <Typography variant="body2" noWrap sx={{ fontSize: '0.85rem' }}>
          {node.title}
        </Typography>
      </Box>
      {!isCollapsed &&
        node.children.map((child) => (
          <TreeRow
            key={child.id}
            node={child}
            depth={depth + 1}
            checked={checked}
            collapsed={collapsed}
            activeId={activeId}
            onToggleCheck={onToggleCheck}
            onToggleCollapse={onToggleCollapse}
            onActivate={onActivate}
          />
        ))}
    </>
  );
};

const DocumentPreviewDialog = ({
  open,
  documentId,
  documentName,
  selectedHeaders,
  onApply,
  onClose,
}: DocumentPreviewDialogProps) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['documentPreview', documentId],
    queryFn: () => fetchDocumentPreview(documentId as string),
    enabled: open && !!documentId,
    staleTime: 60_000,
  });

  const tree = useMemo(() => buildHeaderTree(data?.headers ?? []), [data]);
  const allIds = useMemo(() => (data?.headers ?? []).map((h) => h.id), [data]);

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string | null>(null);

  // Инициализация выбора при открытии/загрузке: undefined = выбрано всё
  useEffect(() => {
    if (!open || allIds.length === 0) return;
    setChecked(new Set(selectedHeaders && selectedHeaders.length > 0 ? selectedHeaders : allIds));
    setActiveId(allIds[0] ?? null);
    setCollapsed(new Set());
    // selectedHeaders намеренно фиксируем на момент открытия
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allIds]);

  const toggleCheck = (node: HeaderTreeNode) => {
    const subtree = [node.id, ...getDescendantIds(node)];
    setChecked((prev) => {
      const next = new Set(prev);
      const allOn = subtree.every((id) => next.has(id));
      subtree.forEach((id) => (allOn ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const toggleCollapse = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApply = () => {
    // Всё или ничего = весь документ (контракт бэка: пустые headers не шлём)
    const isWholeDocument = checked.size === 0 || checked.size === allIds.length;
    onApply(isWholeDocument ? undefined : Array.from(checked));
    onClose();
  };

  const activeHeader = data?.headers.find((h) => h.id === activeId) ?? null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <Typography component="span" noWrap sx={{ fontWeight: 600 }}>
          {data?.name ?? documentName ?? 'Документ'}
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary">
          {checked.size === allIds.length || checked.size === 0
            ? 'весь документ'
            : `выбрано разделов: ${checked.size} из ${allIds.length}`}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size="small">
          <X size={16} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', gap: 2, height: 480, p: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError ? (
          <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="error" variant="body2">
              Не удалось загрузить превью документа
            </Typography>
          </Box>
        ) : allIds.length === 0 ? (
          <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Предосмотр недоступен для этого документа
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                width: '42%',
                overflowY: 'auto',
                p: 1.5,
                borderRight: 1,
                borderColor: 'divider',
              }}
            >
              {tree.map((node) => (
                <TreeRow
                  key={node.id}
                  node={node}
                  depth={0}
                  checked={checked}
                  collapsed={collapsed}
                  activeId={activeId}
                  onToggleCheck={toggleCheck}
                  onToggleCollapse={toggleCollapse}
                  onActivate={setActiveId}
                />
              ))}
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              {activeHeader ? (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {activeHeader.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}
                  >
                    {activeHeader.text}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Выберите раздел слева для просмотра
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="contained" onClick={handleApply} disabled={isLoading || isError}>
          Применить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewDialog;
